import { inject, Injectable } from '@angular/core';
import { AuthProvider } from './auth-provider.interface';
import { LocalStorageService } from '../storage/local-storage.service';
import { AuthRequiredPermissions } from './auth-required-permissions';
import { OKTA_MS_AUTH } from './auth-factory.service';
import { IDToken } from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class MsAuthProvider implements AuthProvider {
  private readonly oktaAuth = inject(OKTA_MS_AUTH);
  private readonly localStorageService = inject(LocalStorageService);

  async signIn(): Promise<boolean> {
    const requiredPermissions = this.localStorageService.get(
      'auth-required-permissions',
    ) as AuthRequiredPermissions;

    const isAuthenticated = await this.isAuthenticated();
    if (
      requiredPermissions.msRequiredPermissions &&
      requiredPermissions.msRequiredPermissions.length &&
      !isAuthenticated
    ) {
      await this.oktaAuth.signInWithRedirect({
        scopes: ['offline_access', ...requiredPermissions.msRequiredPermissions],
        state: 'msRedirect',
      });
      return Promise.resolve(false);
    }

    return Promise.resolve(true);
  }

  isAuthenticated(): Promise<boolean> {
    // MS doesn't provide an ID token, so we use the existence of the refresh token instead
    return Promise.resolve(!!this.oktaAuth.getRefreshToken());
  }

  getAccessToken(): Promise<string | null> {
    return this.oktaAuth.getOrRenewAccessToken();
  }

  async parseFromUrl(): Promise<void> {
    const tokenContainer = await this.oktaAuth.token.parseFromUrl();
    if (!tokenContainer?.tokens) {
      return;
    }
    this.oktaAuth.tokenManager.setTokens(tokenContainer.tokens);
  }

  logout(): Promise<boolean> {
    return this.oktaAuth.signOut({ clearTokensBeforeRedirect: true });
  }

  clear(): void {
    this.oktaAuth.tokenManager.clear();
  }

  async getScopes(): Promise<string[]> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.accessToken?.scopes ?? [];
  }

  async getIdToken(): Promise<IDToken | undefined> {
    const tokens = await this.oktaAuth.tokenManager.getTokens();
    return tokens?.idToken;
  }
}
